import { Box } from "@mui/material";
import React from "react";

const ProductHealthDashboard = ({ sidebarOpen = true }) => {
  const IconWrapper = ({ children }) => (
    <Box
      sx={{
        width: "32px",
        height: "32px",
        backgroundColor: "#f3f4f6",
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginRight: "12px",
      }}
    >
      {children}
    </Box>
  );

  const ClockIcon = () => (
    <IconWrapper>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
      >
        <circle cx="12" cy="12" r="10" />
        <polyline points="12,6 12,12 16,14" />
      </svg>
    </IconWrapper>
  );

  const ScheduleIcon = () => <ClockIcon />;
  const PeopleIcon = () => (
    <IconWrapper>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
      >
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    </IconWrapper>
  );

  const SpeedIcon = () => (
    <IconWrapper>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#6b7280"
        strokeWidth="2"
      >
        <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
      </svg>
    </IconWrapper>
  );

  const metrics = [
    {
      id: 1,
      title: "Average Time Spent",
      value: "3.5 hr",
      change: "+23%",
      changeText: "Increased over the past month",
      icon: <ClockIcon />,
      isPositive: true,
    },
    {
      id: 2,
      title: "Peak Usage Time",
      value: "2PM - 5pm",
      change: "+23%",
      changeText: "Increased over the past month",
      icon: <ScheduleIcon />,
      isPositive: true,
      hasChart: true,
    },
    {
      id: 3,
      title: "Daily Active Users",
      value: "540",
      change: "+23%",
      changeText: "Increased over the past month",
      icon: <PeopleIcon />,
      isPositive: true,
    },
    {
      id: 4,
      title: "Average Page Load Time",
      value: "540",
      change: "+23%",
      changeText: "Increased over the past month",
      icon: <SpeedIcon />,
      isPositive: false,
    },
  ];

  // Calculate sidebar width based on state
  const sidebarWidth = sidebarOpen ? 200 : 10;

  return (
    <Box
      sx={{
        padding: "32px",
        backgroundColor: "#fafafa",
        ml: {
          xs: "0px",
          sm: "0px",
          md: `${sidebarWidth}px`,
          xl: `${sidebarWidth}px`,
        },
        transition: "margin-left 0.3s ease",
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        position: "relative",
      }}
    >
      <Box sx={{ maxWidth: "1550px", margin: "0 auto" }}>
        <Box sx={{ marginBottom: "32px" }}>
          <h1
            style={{
              fontSize: "18px",
              fontWeight: 700,
              color: "#2B3674",
              textAlign: "left",
              margin: "0 0 8px 0",
              lineHeight: "1.4",
              fontFamily: "Outfit",
            }}
          >
            Product Health & Performance
          </h1>
          <p
            style={{
              color: "#A3AED0",
              fontSize: "14px",
              margin: 0,
              textAlign: "left",
              fontWeight: 400,
              fontFamily: "Outfit",
            }}
          >
            AI Revenues prediction on the based of yearly average revenue growth
          </p>
        </Box>

        {/* Metrics Grid with Blur Effect */}
        <Box style={{ position: "relative" }}>
          <Box
            sx={{
              display: "grid",
              gap: "24px",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(2, 1fr)",
                lg: "repeat(4, 1fr)",
                xl: "repeat(4, 1fr)",
              },
              filter: "blur(3px)",
            }}
          >
            {metrics.map((metric) => (
              <Box
                key={metric.id}
                sx={{
                  backgroundColor: "#ffffff",
                  borderRadius: "12px",
                  padding: "24px",
                  minHeight: "120px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  boxShadow:
                    "0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)",
                  position: "relative",
                }}
              >
                {/* Title first */}
                <Box sx={{ marginBottom: "16px" }}>
                  <h3
                    style={{
                      color: "#2B3674",
                      fontSize: "14px",
                      margin: 0,
                      fontWeight: 500,
                      lineHeight: "1.4",
                      textAlign: "left",
                      fontFamily: "Outfit",
                    }}
                  >
                    {metric.title}
                  </h3>
                </Box>

                {/* Icon and Value inline */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: "16px",
                    flex: 1,
                  }}
                >
                  {metric.icon}
                  <Box
                    sx={{
                      fontSize: "28px",
                      fontWeight: 700,
                      color: "#9048FB",
                      lineHeight: "1.2",
                      fontFamily: "Outfit",
                    }}
                  >
                    {metric.value}
                  </Box>

                  {/* Chart positioned on the right for Peak Usage Time */}
                  {metric.hasChart && (
                    <Box
                      style={{
                        width: "100px",
                        height: "60px",
                        marginTop: "2px",
                        marginLeft: "auto",
                      }}
                    >
                      <svg
                        viewBox="0 0 100 30"
                        style={{ width: "100%", height: "100%" }}
                      >
                        <path
                          d="M5,25 L15,20 L25,15 L35,22 L45,10 L55,8 L65,18 L75,12 L85,20 L95,15"
                          fill="none"
                          stroke="#8b5cf6"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Box>
                  )}
                </Box>

                {/* Change statistics at bottom */}
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "auto",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 700,
                      color: metric.isPositive ? "#05CD99" : "#CC0000",
                      fontFamily: "Outfit",
                    }}
                  >
                    {metric.change}
                  </span>
                  <span
                    style={{
                      color: "#A3AED0",
                      fontSize: "11px",
                      lineHeight: "1.3",
                      margin: 0,
                      fontWeight: 400,
                      fontFamily: "Outfit",
                    }}
                  >
                    {metric.changeText}
                  </span>
                </Box>
              </Box>
            ))}
          </Box>

          {/* Coming Soon Overlay */}
          <Box
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
              borderRadius: "12px",
            }}
          >
            <h3
              style={{
                margin: "0",
                fontSize: "32px",
                fontWeight: "700",
                color: "#2B3674",
                fontFamily: "Outfit",
                textAlign: "center",
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Coming Soon
            </h3>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductHealthDashboard;
